// BackLink: the one way a page links up to the page it sits under ("‹ Spark").
// A small tonal pill above the title, where the eye already is. It always goes to
// its parent, never history.back(): a page opened from a link or a bookmark has no
// useful history, and the parent is what "back" means here. Pages using PageHeader
// pass `back`; a page that draws its own top renders this directly.
// docs/features/page-shell.md, "Back link".

import React from 'react';
import { Link } from 'react-router-dom';
import { IconChevronLeft } from './ui/icons';
import { CONTROL_MOTION, FOCUS, PRESS, STATE } from './ui/motion';

export interface BackLinkProps {
  /** The parent page's route. */
  to: string;
  /** The parent page's name, as the nav calls it ("Spark", "Purchase Orders"). */
  label: string;
  /** Runs before navigating, for a page that must close something on the way out. */
  onClick?: () => void;
  className?: string;
}

// The kit's TOUCH grows 6px a side; this pill is 24px, so on a touch screen it grows 10px
// above and below instead, to the 44px a gloved finger needs.
const HIT = "relative [@media(pointer:coarse)]:before:absolute [@media(pointer:coarse)]:before:-inset-x-1.5 [@media(pointer:coarse)]:before:-inset-y-2.5 [@media(pointer:coarse)]:before:content-['']";

const BackLink: React.FC<BackLinkProps> = ({ to, label, onClick, className = '' }) => (
  <Link
    to={to}
    onClick={onClick}
    aria-label={`Back to ${label}`}
    className={`group inline-flex h-6 max-w-full select-none items-center gap-0.5 rounded-full bg-surface-container pl-1.5 pr-2.5 text-[13px] font-medium leading-5 text-on-surface-variant hover:text-on-surface ${STATE} ${CONTROL_MOTION} ${PRESS} ${FOCUS} ${HIT} ${className}`}
  >
    <span
      aria-hidden="true"
      className="inline-flex flex-none transition-transform duration-spring ease-spring group-hover:-translate-x-0.5 group-focus-visible:-translate-x-0.5 group-active:-translate-x-1 motion-reduce:!transform-none motion-reduce:transition-none"
    >
      <IconChevronLeft className="h-3.5 w-3.5" />
    </span>
    <span className="truncate">{label}</span>
  </Link>
);

export default BackLink;
